import React, { useEffect, useMemo, useState } from "react";
import PopupBackground from "@component/PopupBackground";
import {
  copyReturnOrder,
  fetchCreateReturnOrder,
  fetchUpdateReturnOrder,
  returnOrderState,
  fetchCarrierList,
  copyReturnOrderWithProducts,
  fetchExternalWarehouseList,
  fetchLadingWarehouseList,
} from "@redux/returnOrderSlice";
import { Button, Checkbox, DatePicker, Form, Input, Select, Spin } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import useIntRouteParam from "@hooks/useIntRouteParam";
import moment, { Moment } from "moment";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { WarehouseCode } from "@constant/WarehouseCodeValue";
import { ReturnOrderDetail } from "./interfaces";

const FormWrapper = styled(Form)`
  width: 500px;
  padding: 20px;
  background-color: ${({ theme }) => theme.colorNeutral100};
  display: flex;
  flex-direction: column;
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
    order: 1;
  }
  .ant-form-item-label > label.ant-form-item-required:not(.ant-form-item-required-mark-optional)::after {
    margin: 0;
  }
  .ant-form-item {
    margin-bottom: 5px;
  }
`;
const GrayText = styled.span`
  color: ${({ theme }) => theme.colorNeutral500};
`;
const NoticeText = styled.div`
  color: ${({ theme }) => theme.colorSecondary500};
`;
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;

type Props = {
  onClose: () => void;
  returnOrderDetail?: ReturnOrderDetail | null;
  mode?: string;
};

export default function EditOrderForm(props: Props) {
  const { onClose, returnOrderDetail, mode } = props;
  const dispatch = useDispatch();
  const { isFetching, carrierList, externalWarehouseList, ladingWarehouseList } = useSelector(returnOrderState);
  const returnOrderId = useIntRouteParam("returnOrderId");
  const [form] = Form.useForm();
  const [isCopyProduct, setIsCopyProduct] = useState<boolean>(false);
  const sourceWarehouseForm = Form.useWatch("sourceWarehouseCode", form);
  const destWarehouseForm = Form.useWatch("destWarehouseCode", form);
  const isDestWarehouseOEM = destWarehouseForm === "OEM";

  // 黑貓倉新增/複製調撥單限制暫緩處理，待 CU-86eyge020 執行時再處理 https://app.clickup.com/t/25679206/86eyge020

  // 入庫倉選項
  const destWarehouseOption = ladingWarehouseList.filter((item) => {
    const sourceCode = sourceWarehouseForm?.split("-")[0];
    const isSourceDepart = sourceCode === WarehouseCode.DEPART;

    // 通用規則：不能顯示自己
    if (item.code === sourceCode) {
      return false;
    }

    // 按入庫倉類別決定
    // 委外加工倉只能從黑貓、藍田或百貨倉調撥
    if (item.code === WarehouseCode.OEM) {
      return [WarehouseCode.BLACK_CAT, WarehouseCode.REN_TRAP].includes(sourceCode) || isSourceDepart;
    }

    // 按出庫倉類別決定
    switch (sourceCode) {
      // 百貨倉只能調撥到黑貓、藍田
      case WarehouseCode.DEPART:
        return item.code === WarehouseCode.BLACK_CAT || item.code === WarehouseCode.REN_TRAP;

      // 黑貓、藍田倉能調撥到任何倉
      case WarehouseCode.BLACK_CAT:
      case WarehouseCode.REN_TRAP:
        return true;

      // 其他倉不能調撥到委外加工倉
      default:
        return item.code !== WarehouseCode.OEM;
    }
  });

  const sourceWarehouseOptions = useMemo(
    () =>
      externalWarehouseList
        // 配合後端要求DEPART倉庫的code都相同，所以要加上name一起做為value
        .map((item) => ({
          label: item.name,
          value: item.code === "DEPART" ? `${item.code}-${item.name}` : item.code,
        })),
    [externalWarehouseList],
  );

  const onSubmit = (values: any) => {
    // 配合後端要求不另開pos store API, 所以在同時在warehouseList中加入posStore資訊, 但code還是DEPART, 所以只能用name比對, 在submit時要再轉成code
    const destWarehouse = ladingWarehouseList.find(
      (item) => item.code === values.destWarehouseCode || item.name === values.destWarehouseCode,
    );
    if (!destWarehouse) return;
    const [sourceWarehouseCode, sourceWarehouseName] = values.sourceWarehouseCode.split("-");
    const estArrivedDate = values.estArrivedDate ? values.estArrivedDate.format("YYYY-MM-DD") : "";
    const estDeployedDate = values.estDeployedDate ? values.estDeployedDate.format("YYYY-MM-DD") : "";

    const params = {
      estArrivedDate,
      estDeployedDate,
      sourceWarehouseCode,
      sourceWarehouseName,
      destWarehouseCode: destWarehouse.code,
      desc: values.desc,
      carrierId: values.carrierId,
      note: values.note,
      destWarehouseName: destWarehouse.code === "DEPART" ? destWarehouse.name : undefined,
      receiverName: values.receiverName,
      receiverPhone: values.receiverPhone,
      receiverAddress: values.receiverAddress,
    };

    if (mode === "copy" && returnOrderId) {
      if (isCopyProduct) {
        const copyParams = {
          returnOrderId,
          estArrivedDate,
          estDeployedDate,
          desc: values.desc,
          note: values.note,
        };
        dispatch(copyReturnOrderWithProducts(copyParams));
      } else {
        dispatch(copyReturnOrder({ returnOrderId, ...params }));
      }
      onClose();
      return;
    }
    if (returnOrderDetail && returnOrderId) {
      // 編輯
      dispatch(fetchUpdateReturnOrder({ returnOrderId, ...params }));
    } else {
      // 新增
      dispatch(fetchCreateReturnOrder(params));
    }
    onClose();
  };

  const handleOnSelectDestWarehouse = (value: string) => {
    let selectedValue = ladingWarehouseList.find((ladingWarehouse) => {
      return ladingWarehouse.code === value;
    });

    if (!selectedValue) {
      selectedValue = ladingWarehouseList.find((ladingWarehouse) => {
        return ladingWarehouse.name === value;
      });
    }
    if (selectedValue) {
      form.setFieldsValue({
        receiverName: selectedValue.contact,
        receiverPhone: selectedValue.phone,
        receiverAddress: selectedValue.address,
      });
    } else {
      form.setFieldsValue({
        receiverName: undefined,
        receiverPhone: undefined,
        receiverAddress: undefined,
      });
    }
  };

  const disabledDate = (currentDate: Moment) => {
    return currentDate && currentDate < moment().startOf("day");
  };

  const receiverFields = [
    { name: "receiverName", label: "收件人" },
    { name: "receiverPhone", label: "收件電話" },
    { name: "receiverAddress", label: "收件地址" },
  ];

  const renderReceiverFields = () => {
    return receiverFields.map((field) => {
      let inputElement;

      if (!destWarehouseForm) {
        inputElement = <GrayText>選擇入庫倉庫後，系統會自動帶入</GrayText>;
      } else if (isDestWarehouseOEM) {
        inputElement = <Input />;
      } else {
        inputElement = <Input bordered={false} disabled />;
      }

      return (
        <Form.Item key={field.name} label={field.label} name={field.name}>
          {inputElement}
        </Form.Item>
      );
    });
  };

  useEffect(() => {
    if (sourceWarehouseForm) {
      dispatch(fetchCarrierList(sourceWarehouseForm.split("-")[0]));
    }
  }, [dispatch, sourceWarehouseForm]);

  useEffect(() => {
    dispatch(fetchExternalWarehouseList());
    dispatch(fetchLadingWarehouseList());
  }, [dispatch]);

  return (
    <PopupBackground close={onClose}>
      <FormWrapper
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 20 }}
        labelAlign="left"
        onFinish={onSubmit}
        colon={false}
        initialValues={{
          ...returnOrderDetail,
          sourceWarehouseCode:
            returnOrderDetail?.sourceWarehouseCode === "DEPART"
              ? `DEPART-${returnOrderDetail.sourceWarehouseName}`
              : returnOrderDetail?.sourceWarehouseCode,
          destWarehouseCode:
            returnOrderDetail?.destWarehouseCode === "DEPART"
              ? returnOrderDetail.destWarehouseName
              : returnOrderDetail?.destWarehouseCode,
          estDeployedDate: returnOrderDetail?.estDeployedDate ? moment(returnOrderDetail.estDeployedDate) : "",
          estArrivedDate: returnOrderDetail?.estArrivedDate ? moment(returnOrderDetail.estArrivedDate) : "",
        }}
      >
        <Spin spinning={isFetching}>
          {returnOrderId && mode === "copy" && (
            <Form.Item>
              <Checkbox
                onChange={(e: CheckboxChangeEvent) => {
                  const { checked } = e.target;
                  setIsCopyProduct(checked);
                }}
                checked={isCopyProduct}
              >
                要一併複製調撥商品
              </Checkbox>
            </Form.Item>
          )}
          <Form.Item name="sourceWarehouseCode" label="出庫倉庫" rules={[{ required: true, message: "" }]}>
            <Select
              placeholder="請選擇"
              style={{ width: 200 }}
              onChange={() =>
                form.setFieldsValue({
                  carrierRelationName: undefined,
                  carrierId: undefined,
                  destWarehouseCode: undefined,
                })
              }
              disabled={!!returnOrderDetail}
              options={sourceWarehouseOptions}
            />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const estArrivedDateForm = getFieldValue("estArrivedDate");
              return (
                <Form.Item
                  shouldUpdate
                  name="estDeployedDate"
                  label="預計拋單日期"
                  extra={<NoticeText>*此資訊用於顯示倉庫備註中，僅供倉庫人員參考</NoticeText>}
                  rules={[
                    { required: true, message: "" },
                    {
                      validator: (rule, value) => {
                        if (estArrivedDateForm) {
                          return value < moment(estArrivedDateForm) ? Promise.resolve() : Promise.reject();
                        }
                        return Promise.resolve();
                      },
                      message: "預計到貨日必須大於或等於預計拋單日",
                    },
                  ]}
                >
                  <DatePicker placeholder="請選擇日期" style={{ width: 200 }} disabledDate={disabledDate} />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const estDeployedDateForm = getFieldValue("estDeployedDate");
              return (
                <Form.Item
                  shouldUpdate
                  name="estArrivedDate"
                  label="預計到貨日"
                  extra={<NoticeText>*此資訊用於顯示倉庫備註中，僅供倉庫人員參考</NoticeText>}
                  rules={[
                    {
                      validator: (rule, value) => {
                        if (estDeployedDateForm && value) {
                          return value > moment(estDeployedDateForm) ? Promise.resolve() : Promise.reject();
                        }
                        return Promise.resolve();
                      },
                      message: "預計到貨日必須大於或等於預計拋單日",
                    },
                  ]}
                >
                  <DatePicker placeholder="請選擇日期" style={{ width: 200 }} disabledDate={disabledDate} />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item label="運送方式" name="carrierId" rules={[{ required: true, message: "" }]}>
            <Select placeholder="請選擇" style={{ width: 200 }} disabled={!sourceWarehouseForm}>
              {carrierList.map((carrier) => (
                <Select.Option key={carrier.id} value={carrier.carrierId}>
                  {carrier.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="入庫倉庫" name="destWarehouseCode" rules={[{ required: true, message: "" }]}>
            <Select
              placeholder="請選擇"
              style={{ width: 200 }}
              onChange={handleOnSelectDestWarehouse}
              disabled={!sourceWarehouseForm || !!returnOrderDetail}
            >
              {destWarehouseOption.map((item) => {
                return (
                  // 配合後端要求不另開pos store API, 所以在同時在warehouseList中加入posStore資訊, 但code還是DEPART, 所以只能用name比對, 在submit時要再轉成code
                  <Select.Option key={item.name} value={item.code === "DEPART" ? item.name : item.code}>
                    {item.name}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
          {renderReceiverFields()}
          <Form.Item label="給倉庫的備註" name="note">
            <Input placeholder="給倉庫的備註，最多 30 字" maxLength={50} />
          </Form.Item>
          <Form.Item label="備註" name="desc">
            <Input placeholder="最多30字" maxLength={50} />
          </Form.Item>
          <Footer>
            <Button style={{ marginRight: "14px" }} onClick={() => onClose()}>
              取消
            </Button>
            <Button type="primary" htmlType="submit">
              儲存
            </Button>
          </Footer>
        </Spin>
      </FormWrapper>
    </PopupBackground>
  );
}
